import { z } from 'zod';
import { diagram, type Diagram } from './diagram.js';

/**
 * Drills, sessions, templates and plans: inputs, response shapes and the
 * maths for timings and warnings. Shared so the builder shows exactly what
 * the server stores.
 */

// ---------- Tags ----------

export const STARTER_TAGS = [
  'Shooting',
  'Ball handling',
  'Passing',
  'Defence',
  'Rebounding',
  'Footwork',
  'Transition',
  'Offence',
  'Conditioning',
  'Plyometrics',
  'Strength',
  'Games',
] as const;

const tag = z
  .string()
  .trim()
  .min(1)
  .max(30, 'Keep each tag under 30 characters.')
  .transform((t) => {
    const starter = STARTER_TAGS.find((s) => s.toLowerCase() === t.toLowerCase());
    return starter ?? t;
  });

const tags = z
  .array(tag)
  .max(10, 'Use up to 10 tags.')
  .default([])
  .transform((list) => [...new Map(list.map((t) => [t.toLowerCase(), t])).values()]);

// ---------- Small helpers ----------

const text = (max: number, label: string) =>
  z
    .string()
    .trim()
    .max(max, `Keep the ${label} under ${max.toLocaleString('en-AU')} characters.`);

/** Optional text: empty becomes null. */
const optText = (max: number, label: string) =>
  text(max, label)
    .nullish()
    .transform((v) => (v ? v : null));

/** For updates: left out stays as is, empty clears. */
const editText = (max: number, label: string) =>
  text(max, label)
    .nullable()
    .optional()
    .transform((v) => (v === undefined ? undefined : v || null));

const minutes = z.coerce
  .number({ invalid_type_error: 'Enter the number of minutes.' })
  .int('Use whole minutes.')
  .min(1, 'A drill needs at least 1 minute.')
  .max(180, 'Keep a single drill under 3 hours.');

const optCount = (max: number, label: string) =>
  z.coerce
    .number({ invalid_type_error: `Enter a number for ${label}.` })
    .int(`Use a whole number for ${label}.`)
    .min(1, `${cap(label)} must be at least 1.`)
    .max(max, `${cap(label)} can’t be more than ${max}.`)
    .nullish()
    .transform((v) => v ?? null);

function cap(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const title = (label: string) => z.string().trim().min(1, `Give the ${label} a name.`).max(120, `Keep the ${label} name under 120 characters.`);

export const dateString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Choose a date.')
  .refine((d) => !Number.isNaN(new Date(`${d}T00:00:00Z`).getTime()), 'Choose a real date.');

export const timeString = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Choose a start time, like 16:30.');

// ---------- Drills ----------

const drillFields = {
  title: title('drill'),
  description: optText(4000, 'description'),
  coachingPoints: optText(4000, 'coaching points'),
  equipment: optText(500, 'equipment list'),
  minPlayers: optCount(60, 'minimum players'),
  maxPlayers: optCount(60, 'maximum players'),
  courts: optCount(6, 'courts'),
  minutes: minutes.default(10),
  tags,
  diagram: diagram.nullish().transform((v) => v ?? null),
};

export const createDrillInput = z
  .object(drillFields)
  .refine((d) => !d.minPlayers || !d.maxPlayers || d.minPlayers <= d.maxPlayers, {
    message: 'Minimum players can’t be more than maximum players.',
    path: ['maxPlayers'],
  });
export type CreateDrillInput = z.input<typeof createDrillInput>;

export const updateDrillInput = z.object({
  title: title('drill').optional(),
  description: editText(4000, 'description'),
  coachingPoints: editText(4000, 'coaching points'),
  equipment: editText(500, 'equipment list'),
  minPlayers: optCount(60, 'minimum players').optional(),
  maxPlayers: optCount(60, 'maximum players').optional(),
  courts: optCount(6, 'courts').optional(),
  minutes: minutes.optional(),
  tags: tags.optional(),
  diagram: diagram.nullable().optional(),
});
export type UpdateDrillInput = z.input<typeof updateDrillInput>;

export const drillSharesInput = z.object({ teamIds: z.array(z.string().uuid()).max(50) });

export interface Drill {
  id: string;
  title: string;
  description: string | null;
  coachingPoints: string | null;
  equipment: string | null;
  minPlayers: number | null;
  maxPlayers: number | null;
  courts: number | null;
  minutes: number;
  tags: string[];
  diagram: Diagram | null;
  owner: { id: string; name: string };
  /** True if the signed-in coach owns it (and can edit it). */
  mine: boolean;
  /** Teams it's shared with that the signed-in coach can see. */
  sharedWith: { teamId: string; teamName: string }[];
  createdAt: string;
  updatedAt: string;
}

// ---------- Plans ----------

/** One drill in a plan. A copy: changing it never changes the library. */
export const planItem = z.object({
  id: z.string().uuid(),
  sourceDrillId: z.string().uuid().nullish().transform((v) => v ?? null),
  title: title('drill'),
  minutes,
  /** Who does this when several drills run at once, e.g. "Guards" or "Court 2". */
  group: optText(40, 'group name'),
  description: optText(4000, 'description'),
  coachingPoints: optText(4000, 'coaching points'),
  equipment: optText(500, 'equipment list'),
  /** Coach-only note for this session. */
  notes: optText(2000, 'note'),
  minPlayers: optCount(60, 'minimum players'),
  courts: optCount(6, 'courts'),
  /** Skill tags, copied from the drill; used for season coverage. */
  tags: z.array(tag).max(10).nullish().transform((v) => v ?? []),
  diagram: diagram.nullish().transform((v) => v ?? null),
});
export type PlanItem = z.output<typeof planItem>;

/** A time slot. Its drills run at the same time; it lasts as long as the longest. */
export const planBlock = z.object({
  id: z.string().uuid(),
  items: z.array(planItem).min(1, 'Every time slot needs at least one drill.').max(6, 'Run up to 6 drills at once.'),
});
export type PlanBlock = z.output<typeof planBlock>;

/** Longest drink break between parts, in minutes. */
export const MAX_BREAK = 2;
/** Break given to new sessions and templates. */
export const DEFAULT_BREAK = 2;

export const planSection = z.object({
  id: z.string().uuid(),
  title: z.string().trim().min(1, 'Give each part of the session a name.').max(60, 'Keep part names under 60 characters.'),
  blocks: z.array(planBlock).max(40),
  /** Drink break after this part, 0–2 minutes. Older plans have none. */
  breakAfter: z.coerce.number().int().min(0).max(MAX_BREAK, `Keep drink breaks to ${MAX_BREAK} minutes or less.`).nullish().transform((v) => v ?? 0),
});
export type PlanSection = z.output<typeof planSection>;

export const plan = z
  .object({ sections: z.array(planSection).max(12, 'Use up to 12 parts in a session.') })
  .refine((p) => p.sections.reduce((n, s) => n + s.blocks.length, 0) <= 80, 'That plan is too long. Use up to 80 time slots.');
export type Plan = z.output<typeof plan>;

export const savePlanInput = z.object({
  /** The version you started editing. If someone saved since, you get a conflict instead of overwriting them. */
  version: z.number().int().min(1),
  plan,
});
export type SavePlanInput = z.input<typeof savePlanInput>;

/**
 * Reads a plan stored by any version of Hoopify, filling in fields added later
 * (tags, diagram…). Falls back to the stored value if it somehow doesn't parse,
 * so a bad field never hides a coach's plan.
 */
export function readPlan(stored: unknown): Plan {
  const r = plan.safeParse(stored);
  if (r.success) return r.data;
  const raw = (stored ?? { sections: [] }) as Plan;
  return {
    sections: (raw.sections ?? []).map((s) => ({
      ...s,
      breakAfter: Math.min(MAX_BREAK, Math.max(0, Math.round(Number(s.breakAfter) || 0))),
      blocks: (s.blocks ?? []).map((b) => ({ ...b, items: (b.items ?? []).map((i) => ({ ...i, tags: Array.isArray(i.tags) ? i.tags : [], diagram: i.diagram ?? null })) })),
    })),
  };
}

export const DEFAULT_SECTIONS = ['Warm-up', 'Skills', 'Team', 'Scrimmage', 'Cool-down'] as const;

// ---------- Sessions and templates ----------

const sessionLength = z.coerce
  .number({ invalid_type_error: 'Enter the session length in minutes.' })
  .int('Use whole minutes.')
  .min(10, 'A session needs to be at least 10 minutes.')
  .max(360, 'Keep a session under 6 hours.');

const sessionFields = {
  title: title('session'),
  durationMinutes: sessionLength,
  location: optText(120, 'location'),
  focus: optText(200, 'focus'),
  whatToBring: optText(500, 'what to bring'),
  notes: optText(4000, 'notes'),
  playersExpected: optCount(60, 'players expected'),
  courtsAvailable: optCount(6, 'courts available'),
};

export const createSessionInput = z.object({
  ...sessionFields,
  date: dateString,
  startTime: timeString,
  /** Start from a template's plan and details. */
  templateId: z.string().uuid().optional(),
});
export type CreateSessionInput = z.input<typeof createSessionInput>;

export const updateSessionInput = z.object({
  title: title('session').optional(),
  date: dateString.optional(),
  startTime: timeString.optional(),
  durationMinutes: sessionLength.optional(),
  location: editText(120, 'location'),
  focus: editText(200, 'focus'),
  whatToBring: editText(500, 'what to bring'),
  notes: editText(4000, 'notes'),
  playersExpected: optCount(60, 'players expected').optional(),
  courtsAvailable: optCount(6, 'courts available').optional(),
  status: z.enum(['draft', 'published']).optional(),
});
export type UpdateSessionInput = z.input<typeof updateSessionInput>;

export const copySessionInput = z.object({
  teamId: z.string().uuid('Choose a team.'),
  date: dateString,
  startTime: timeString.optional(),
});
export type CopySessionInput = z.input<typeof copySessionInput>;

export const createTemplateInput = z.object({
  ...sessionFields,
  /** Copy the plan from this session. */
  fromSessionId: z.string().uuid().optional(),
});
export type CreateTemplateInput = z.input<typeof createTemplateInput>;

export const updateTemplateInput = updateSessionInput.omit({ date: true, startTime: true, status: true });
export type UpdateTemplateInput = z.input<typeof updateTemplateInput>;

export const scheduleQuery = z.object({ from: dateString, to: dateString }).refine((q) => q.from <= q.to, 'The start date must be before the end date.');

export type SessionStatus = 'draft' | 'published';

export interface SessionSummary {
  id: string;
  teamId: string;
  teamName: string;
  title: string;
  date: string;
  startTime: string;
  durationMinutes: number;
  location: string | null;
  focus: string | null;
  status: SessionStatus;
  /** Minutes the plan adds up to. */
  plannedMinutes: number;
  drillCount: number;
}

/** What coaches (and read-only members) get. */
export interface SessionDetail extends SessionSummary {
  view: 'full';
  whatToBring: string | null;
  notes: string | null;
  playersExpected: number | null;
  courtsAvailable: number | null;
  plan: Plan;
  version: number;
  canEdit: boolean;
  publishedAt: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

/** What players and parents get: no coaching points, notes or drafts. */
export interface SessionPlayerView extends SessionSummary {
  view: 'player';
  whatToBring: string | null;
  plan: { sections: { title: string; breakAfter?: number; blocks: { minutes: number; items: { title: string; minutes: number; group: string | null; diagram: Diagram | null }[] }[] }[] };
}

export interface TemplateSummary {
  id: string;
  title: string;
  durationMinutes: number;
  focus: string | null;
  plannedMinutes: number;
  drillCount: number;
  updatedAt: string;
}

export interface TemplateDetail extends TemplateSummary {
  location: string | null;
  whatToBring: string | null;
  notes: string | null;
  playersExpected: number | null;
  courtsAvailable: number | null;
  plan: Plan;
  version: number;
}

// ---------- Plan maths ----------

export function blockMinutes(block: { items: { minutes: number }[] }) {
  return block.items.reduce((m, i) => Math.max(m, i.minutes), 0);
}

type Shape = { sections: { breakAfter?: number | null; blocks: { items: { minutes: number }[] }[] }[] };

/**
 * The drink break that actually happens after each part: only between two parts
 * that both have drills (an empty part, or the last one, has no break after it).
 */
export function sectionBreaks(p: Shape): number[] {
  return p.sections.map((s, i) => {
    if (!s.blocks.length || !s.breakAfter) return 0;
    const later = p.sections.slice(i + 1).some((x) => x.blocks.length);
    return later ? Math.min(MAX_BREAK, s.breakAfter) : 0;
  });
}

export function breakMinutes(p: Shape) {
  return sectionBreaks(p).reduce((a, b) => a + b, 0);
}

/** Drill time only (what the season's skill coverage counts). */
export function drillMinutes(p: Shape) {
  return p.sections.reduce((t, s) => t + s.blocks.reduce((u, b) => u + blockMinutes(b), 0), 0);
}

/** Everything that takes time: drills plus drink breaks. */
export function planMinutes(p: Shape) {
  return drillMinutes(p) + breakMinutes(p);
}

export function drillCount(p: { sections: { blocks: { items: unknown[] }[] }[] }) {
  return p.sections.reduce((t, s) => t + s.blocks.reduce((u, b) => u + b.items.length, 0), 0);
}

/** "16:30" + 75 → "17:45" (wraps past midnight). */
export function addMinutes(time: string, mins: number) {
  const [h, m] = time.split(':').map(Number) as [number, number];
  const total = (((h * 60 + m + mins) % 1440) + 1440) % 1440;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/** Start time of every block, keyed by block id. */
export function blockStartTimes(p: Plan, startTime: string) {
  const out = new Map<string, string>();
  const breaks = sectionBreaks(p);
  let offset = 0;
  p.sections.forEach((s, i) => {
    for (const b of s.blocks) {
      out.set(b.id, addMinutes(startTime, offset));
      offset += blockMinutes(b);
    }
    // The break after a part starts here; keyed by the part's id.
    if (breaks[i]) out.set(`break:${s.id}`, addMinutes(startTime, offset));
    offset += breaks[i]!;
  });
  return out;
}

export interface PlanWarning {
  /** Block the warning is about, if any. */
  blockId: string | null;
  message: string;
}

/** Things worth a coach's attention. Never blocks saving. */
export function planWarnings(
  p: Plan,
  s: { durationMinutes: number; courtsAvailable: number | null; playersExpected: number | null },
): PlanWarning[] {
  const out: PlanWarning[] = [];
  const total = planMinutes(p);
  if (total > s.durationMinutes) {
    out.push({ blockId: null, message: `The plan runs ${total - s.durationMinutes} min over the ${s.durationMinutes}-minute session.` });
  }
  for (const section of p.sections) {
    for (const b of section.blocks) {
      const courtsNeeded = b.items.reduce((n, i) => n + (i.courts ?? 1), 0);
      if (s.courtsAvailable && courtsNeeded > s.courtsAvailable) {
        out.push({
          blockId: b.id,
          message:
            b.items.length > 1
              ? `${b.items.length} drills at once need ${courtsNeeded} courts, but you have ${s.courtsAvailable}.`
              : `“${b.items[0]!.title}” needs ${courtsNeeded} courts, but you have ${s.courtsAvailable}.`,
        });
      }
      if (s.playersExpected) {
        const playersNeeded = b.items.reduce((n, i) => n + (i.minPlayers ?? 0), 0);
        if (playersNeeded > s.playersExpected) {
          out.push({
            blockId: b.id,
            message:
              b.items.length > 1
                ? `These drills need at least ${playersNeeded} players together, but you expect ${s.playersExpected}.`
                : `“${b.items[0]!.title}” needs at least ${playersNeeded} players, but you expect ${s.playersExpected}.`,
          });
        }
      }
    }
  }
  return out;
}

/** Strips everything players and parents shouldn't see. */
export function playerPlan(p: Plan): SessionPlayerView['plan'] {
  const breaks = sectionBreaks(p);
  return {
    sections: p.sections
      .map((s, i) => ({ s, breakAfter: breaks[i]! }))
      .filter(({ s }) => s.blocks.length)
      .map(({ s, breakAfter }) => ({
        title: s.title,
        breakAfter,
        blocks: s.blocks.map((b) => ({
          minutes: blockMinutes(b),
          items: b.items.map((i) => ({ title: i.title, minutes: i.minutes, group: i.group, diagram: i.diagram })),
        })),
      })),
  };
}

/** Week starting Monday (Australian convention) containing `date` (YYYY-MM-DD). */
export function weekStart(date: string) {
  const d = new Date(`${date}T00:00:00Z`);
  const dow = (d.getUTCDay() + 6) % 7; // Mon=0
  d.setUTCDate(d.getUTCDate() - dow);
  return d.toISOString().slice(0, 10);
}

export function addDays(date: string, days: number) {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

// ---------- Hoopify drill library (ships with the app, read-only) ----------

export const LIBRARY_CATEGORIES = [
  'Warm-up',
  'Ball handling',
  'Passing',
  'Shooting',
  'Finishing',
  'Footwork',
  'Defence',
  'Rebounding',
  'Transition',
  'Team offence',
  'Conditioning',
  'Plyometrics',
  'Strength and mobility',
  'At home',
  'Games',
] as const;
export type LibraryCategory = (typeof LIBRARY_CATEGORIES)[number];

export const LIBRARY_LEVELS = ['juniors', 'all', 'older'] as const;
export type LibraryLevel = (typeof LIBRARY_LEVELS)[number];
export const LEVEL_LABELS: Record<LibraryLevel, string> = {
  juniors: 'Juniors (U8–U12)',
  all: 'All ages',
  older: 'U14 and up',
};

export interface LibraryDrill {
  /** Stable slug, e.g. "mikan-drill". */
  id: string;
  title: string;
  category: LibraryCategory;
  level: LibraryLevel;
  /** One line for the browse list. */
  summary: string;
  description: string;
  coachingPoints: string;
  equipment: string | null;
  minPlayers: number | null;
  maxPlayers: number | null;
  courts: number | null;
  minutes: number;
  tags: string[];
  diagram: Diagram | null;
  /** Ways to make it easier or harder. */
  variations: string | null;
}
